import { Component, computed, DestroyRef, inject, Input, OnInit, signal, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { DemoInstance, DemoInstanceViewmodel, DemoSnapshot, DemoSnapshotStatusEnum, PowerState, ProvisioningState } from "src/app/core";
import { environment } from "src/environments/environment";
import { AppConfigurationService } from "src/app/services/app-configuration.service";
import { TemplateDialogComponent, TemplateDialogData } from "src/app/shared/template-dialog/template-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import notify from "devextreme/ui/notify";
import { NavigationStart, Router } from "@angular/router";
import { confirm } from "devextreme/ui/dialog";
import { DemoInstancesService } from "src/app/services/demo-instances.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";
import { startWith, map, shareReplay, filter, finalize } from "rxjs/operators";
import { DatePipe } from "@angular/common";

@Component({
  selector: "brd-instance-tile",
  templateUrl: "./instance-tile.component.html",
  styleUrl: "./instance-tile.component.less",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  providers: [DatePipe]
})
export class InstanceTileComponent implements OnInit {
  @Input() instance!: DemoInstance;
  @ViewChild("infodialog", { read: TemplateRef })
  infoDialogTemplate!: TemplateRef<TemplateDialogData>;
  @Input() canAccess!: boolean;
  @ViewChild("snapshotdialog", { read: TemplateRef })
  snapshotDialogTemplate!: TemplateRef<TemplateDialogData>;

  isActive$: Observable<boolean | null>;
  activeBadgeText: string;
  activeBadgeClass: string;
  isLoading: boolean = false;
  loadingBtnText: string;

  instanceInfo: { label: string; value: string }[] = [];

  hdpAddress = environment.hdpAddress;
  sleepInterval: number;
  deleteInterval: number;

  accounts = [];
  defaultPassword = "Board365!";

  destroyRef = inject(DestroyRef);

  snapshotStatusEnum = DemoSnapshotStatusEnum;
  snapshotDateFormatted: string;
  demoSnapshot?: DemoSnapshot;

  createSnapshotBtnText: string = "Create snapshot";
  submitting = signal(false);
  canCreateSnapshot = signal(false);
  isActiveSignal = signal(false);
  enableSnaphotButton = computed(() => !this.submitting() && this.canCreateSnapshot() && this.isActiveSignal());

  constructor(
    appConfigService: AppConfigurationService,
    private readonly dialog: MatDialog,
    private router: Router,
    private instanceService: DemoInstancesService,
    private datePipe: DatePipe
  ) {
    appConfigService.getConfigurationSetting("CloudDemoTraining:DemoDeleteInterval").then((setting: { value: number }) => {
      this.deleteInterval = setting.value;
    });
    appConfigService.getConfigurationSetting("CloudDemoTraining:DemoSleepInterval").then((setting: { value: number }) => {
      this.sleepInterval = setting.value;
    });

    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter(event => event instanceof NavigationStart)
      )
      .subscribe(() => this.dialog.closeAll());
  }

  ngOnInit(): void {
    const dateToCompare = new Date(2025, 4, 15);

    if (!!this.instance.creationDate && dateToCompare > new Date(this.instance.creationDate)) {
      this.accounts = ["jane.doe", "john.smith", "paola.poe"];
    } else this.accounts.push(this.instance.owner);

    this.isActive$ = this.instanceService
      .getInstanceAvailability(
        this.instance.id,
        this.instance.name,
        this.instance.regionName,
        this.instance.powerState.toString(),
        this.instance.provisioningState.toString()
      )
      .pipe(
        startWith(null),
        map((available: boolean | null) => {
          if (available !== null) {
            this.instance.available = available;
            const demoInstance = new DemoInstanceViewmodel(this.instance);
            this.isLoading = demoInstance.powerStateText !== "Offline" && demoInstance.powerStateText !== "Online";
            this.loadingBtnText = demoInstance.powerStateText;
            return (
              available && demoInstance.powerState === PowerState.Online && demoInstance.provisioningState === ProvisioningState.Completed
            );
          }
          return null;
        }),
        map(res => {
          this.isActiveSignal.set(res !== null ? res : false);
          return res;
        }),
        shareReplay(1)
      );

    if (
      this.instance.powerState === PowerState.Online ||
      this.instance.powerState === PowerState.Updating ||
      this.instance.powerState === PowerState.Stopping
    ) {
      this.activeBadgeText = "Active";
      this.activeBadgeClass = "sun-16";
    } else if (this.instance.powerState === PowerState.Offline || this.instance.powerState === PowerState.Starting) {
      this.activeBadgeText = "Sleep";
      this.activeBadgeClass = "moon-16";
    }

    this.instanceInfo = [
      { label: "creator", value: this.instance.owner },
      { label: "type", value: this.instance.templateSuggested ? "Suggested" : "Custom" },
      {
        label: "expiration date",
        value: this.instance.expirationDate ? this.datePipe.transform(this.instance.expirationDate, "MM/dd/yyyy") : "-"
      }
    ];

    this.updateSnapshotData(this.instance?.snapshot);
  }

  infoButtonClick(event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      width: "80vw",
      height: "90vh",
      maxWidth: "1250px",
      maxHeight: "700px",
      data: {
        title: `${this.instance.title}`,
        templateVariable: this.infoDialogTemplate,
        password: this.instance.password ?? this.defaultPassword
      }
    });

    //this.startFullScreen = fullScreen;
    dialogRef.afterClosed().subscribe();
  }

  infoSnapshotClick(event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(TemplateDialogComponent, {
      width: "80vw",
      height: "auto",
      maxWidth: "800px",
      data: {
        title: "Snapshot for POC",
        templateVariable: this.snapshotDialogTemplate
      }
    });

    dialogRef.afterClosed().subscribe();
  }

  addOrUpdateSnapshot(): void {
    this.submitting.set(true);
    this.instanceService
      .addOrUpdateSnapshot(this.instance)
      .pipe(
        finalize(() => {
          this.submitting.set(false);
        })
      )
      .subscribe(res => {
        this.updateSnapshotData(res);
      });
  }

  copyTextToClipboard(event: Event, val: string): void {
    if (!navigator.clipboard) {
      const selBox = document.createElement("textarea");
      selBox.style.position = "fixed";
      selBox.style.left = "0";
      selBox.style.top = "0";
      selBox.style.opacity = "0";
      selBox.value = val;
      document.body.appendChild(selBox);
      selBox.focus();
      selBox.select();
      document.execCommand("copy");
      document.body.removeChild(selBox);
      notify({
        message: "Copied",
        type: "success",
        displayTime: 3000,
        maxWidth: 200,
        position: "top center",
        closeOnClick: true
      });
    } else {
      navigator.clipboard.writeText(val).then(function () {
        notify({
          message: "Copied",
          type: "success",
          displayTime: 3000,
          maxWidth: 200,
          position: "top center",
          closeOnClick: true
        });
      });
    }
    event.stopPropagation();
  }

  askSupprt(): void {
    this.dialog.closeAll();
    this.router.navigate(["/support"]);
  }

  openNewWindow(event: Event, url: string): void {
    window.open(url, "_blank");
    event.stopPropagation();
  }

  deleteDemo(event: Event, demoName: string, demoId: string, creator: string): void {
    const res = confirm(`Are you sure to delete <strong>${demoName}</strong> created by <strong>${creator}</strong>?`, "Deletion");
    res.then(dialogResult => {
      if (dialogResult) {
        this.instanceService.deleteDemoInstance(demoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.dialog.closeAll();
      }
    });

    event.stopPropagation();
  }

  stopDemo(event: Event, demoName: string, demoId: string): void {
    const res = confirm(`Are you sure to put the demo <strong>${demoName}</strong> into sleep mode?`, "Sleep mode");
    res.then(dialogResult => {
      if (dialogResult) this.instanceService.stopDemoInstance(demoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      this.dialog.closeAll();
    });

    event.stopPropagation();
  }

  startInstance(instanceId: string): void {
    this.instanceService.restartDemoInstanceById(instanceId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  handleButtonClick(event: Event): void {
    this.isActive$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isActive => {
      if (isActive) {
        this.openNewWindow(event, this.instance.link);
      } else {
        this.startInstance(this.instance.id);
      }
    });
  }

  private updateSnapshotData(snapshot?: DemoSnapshot): void {
    this.demoSnapshot = snapshot;
    this.snapshotDateFormatted = this.demoSnapshot?.snapshotDate
      ? this.datePipe.transform(this.demoSnapshot.snapshotDate, "MM/dd/yyyy 'h' HH:mm")
      : "-";
    if (this.demoSnapshot?.snapshotStatus === DemoSnapshotStatusEnum.ready) this.createSnapshotBtnText = "Update snapshot";
    else if (this.demoSnapshot?.snapshotStatus === DemoSnapshotStatusEnum.error) this.createSnapshotBtnText = "Retry";

    this.canCreateSnapshot.set(!snapshot || snapshot.snapshotStatus !== DemoSnapshotStatusEnum.creating);
  }
}
